import { gql } from "@apollo/client";
import { CopyAll as CopyAllIcon } from "@mui/icons-material";
import { Group as GroupIcon } from "@mui/icons-material";
import { Lock as LockIcon } from "@mui/icons-material";
import { Public as PublicIcon } from "@mui/icons-material";
import { Store as StoreIcon } from "@mui/icons-material";
import {
  Stack,
  StackProps,
  SvgIconProps,
  Theme,
  Tooltip,
  Typography,
  TypographyProps,
  useTheme,
} from "@mui/material";
import type { TFunction } from "@msys/tolgee";
import { useTranslate } from "@tolgee/react";
import { ViewerShapeFragment } from "../../../auth/UserDataProvider.generated.js";
import { useUserData } from "../../../auth/useUserData.js";
import { TemplateQuotePublishingStatus_QuoteTemplateFragment } from "./TemplateQuotePublishingStatus.generated.js";

const getColor = (
  theme: Theme,
  doc: TemplateQuotePublishingStatus_QuoteTemplateFragment,
  viewer: ViewerShapeFragment | null | undefined
) =>
  doc.owningSystemOrganisationId === viewer?.organisation?.id
    ? !doc.hasAnyPublishedVersion
      ? theme.palette.grey[500]
      : doc.templateSharing && doc.templateSharing.sharedPublicly
        ? theme.palette.primary.main
        : doc.templateSharing && !doc.templateSharing.sharedPublicly
          ? theme.palette.primary.main
          : theme.palette.grey[500]
    : theme.palette.secondary.main;

const getIcon = (
  doc: TemplateQuotePublishingStatus_QuoteTemplateFragment,
  viewer: ViewerShapeFragment | null | undefined,
  IconProps?: SvgIconProps
) =>
  doc.owningSystemOrganisationId === viewer?.organisation?.id ? (
    !doc.hasAnyPublishedVersion ? (
      <CopyAllIcon {...IconProps} />
    ) : doc.templateSharing && doc.templateSharing.sharedPublicly ? (
      <PublicIcon {...IconProps} />
    ) : doc.templateSharing && !doc.templateSharing.sharedPublicly ? (
      <GroupIcon {...IconProps} />
    ) : (
      <LockIcon {...IconProps} />
    )
  ) : (
    <StoreIcon {...IconProps} />
  );

const getLabel = (
  doc: TemplateQuotePublishingStatus_QuoteTemplateFragment,
  viewer: ViewerShapeFragment | null | undefined,
  t: TFunction<"Templates" | "TemplatesSharing">
) =>
  doc.owningSystemOrganisationId === viewer?.organisation?.id
    ? !doc.hasAnyPublishedVersion
      ? t("Draft", {
          ns: "Templates",
        })
      : doc.templateSharing && doc.templateSharing.sharedPublicly
        ? t("Public", {
            ns: "TemplatesSharing",
          })
        : doc.templateSharing && !doc.templateSharing.sharedPublicly
          ? t("Protected", {
              ns: "TemplatesSharing",
            })
          : t("Private", {
              ns: "TemplatesSharing",
            })
    : doc.templateSharing && doc.templateSharing.subscribedToTemplate
      ? t("Subscribed", { ns: "Templates" })
      : t("Store", { ns: "Templates" });

interface StatusProps extends TypographyProps {
  showOnlyPublishingStatus?: boolean;
  doc: TemplateQuotePublishingStatus_QuoteTemplateFragment;
  IconProps?: SvgIconProps;
}

export const TemplateQuotePublishingStatus = ({
  showOnlyPublishingStatus = false,
  doc,
  IconProps,
  ...rest
}: StatusProps) => {
  const { t } = useTranslate(["Templates", "TemplatesSharing"]);
  const viewer = useUserData()?.currentUser;
  const theme = useTheme();

  if (
    showOnlyPublishingStatus &&
    (doc.owningSystemOrganisationId !== viewer?.organisation?.id ||
      !doc.hasAnyPublishedVersion)
  )
    return null;

  return (
    <Typography
      component={Stack}
      direction="row"
      spacing={0.5}
      color={getColor(theme, doc, viewer)}
      {...rest}
      sx={[
        {
          alignItems: "center",
        },
        ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx]),
      ]}
    >
      {getIcon(doc, viewer, IconProps)}
      <span>{getLabel(doc, viewer, t)}</span>
    </Typography>
  );
};

interface IconProps extends StackProps {
  showOnlyPublishedStatus?: boolean;
  doc: TemplateQuotePublishingStatus_QuoteTemplateFragment;
  IconProps?: SvgIconProps;
}

export const TemplateQuotePublishingIcon = ({
  showOnlyPublishedStatus = true,
  doc,
  IconProps,
  ...rest
}: IconProps) => {
  const { t } = useTranslate(["Templates", "TemplatesSharing"]);
  const viewer = useUserData()?.currentUser;
  if (doc.owningSystemOrganisationId !== viewer?.organisation?.id) return null;
  if (
    showOnlyPublishedStatus &&
    !doc.hasAnyPublishedVersion &&
    !doc.templateSharing
  )
    return null;

  return (
    <Stack
      direction="row"
      spacing={0}
      {...rest}
      sx={[
        {
          alignItems: "center",
        },
        theme => ({
          color: getColor(theme, doc, viewer),
        }),
        ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx]),
      ]}
    >
      <Tooltip title={getLabel(doc, viewer, t)}>
        {getIcon(doc, viewer, IconProps)}
      </Tooltip>
    </Stack>
  );
};
